import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { RimFallbackSvg } from '~/components/image-selection-tile/components/fallback-svgs/rim-fallback-svg';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { state } from '~/storage';
import { createRecord } from '~/utils/create-record';
import { OptionsHeader } from '../options-header';

export function Rims() {
	const { i18n } = useI18n();
	const { rims } = useCatalog();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const { isLoading, changeConfiguration, loadingFlags, isItemLoading, isItemChecked } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { getName } = useDebugMode();
	const { mediaByProfileId } = useImages();
	const [activeFilters] = useAtom(state.activeFilters);
	const { step, total } = useSteps('rims');

	const trackSelectionClickEvent = useCallback(
		(item: FeatureOrPackage, selected: boolean) => {
			const filters = activeFilters?.rims.length > 0 ? activeFilters?.rims : 'all';

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'image',
				value: `selection feature app: rims: ${filters}`,
				category: 'rims',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[activeFilters?.rims, step, total],
	);

	const { items, filters } = useMemo(() => {
		if (!rims) return { items: [], filters: [] };

		// Divide the rims into categories according to rim size
		const itemsByFamily = createRecord(rims, (rim) => rim.sizeCategory ?? 'Uncategorized');

		// Sort categories in ascending rim size and map for the card component
		const sortedCategories = Object.keys(itemsByFamily).sort((a, b) => {
			/* eslint-disable-next-line radix */
			const parseSize = (size: string) => parseInt(size.replace('"', '') || '0');
			const sizeA = parseSize(a);
			const sizeB = parseSize(b);
			return sizeA - sizeB;
		});

		// Map items for the tile selection
		const mappedItems = rims.map((rim) => ({
			name: getName(rim) ?? ' ',
			key: rim.featureId,
			onSelect: () => {
				trackSelectionClickEvent(rim.__item, false);
				changeConfiguration(rim as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(rim),
			readonly: !rim.status.changeable,
			familyName: rim.sizeCategory ?? '',
			standard: rim.status.standard,
			imgUrl: rim.wheelImageUrl ?? '',
			selected: isItemChecked(rim),
			price: config.showPrices
				? { totalPrice: rim.totalPrice, monthlyRate: rim.monthlyRate, feature }
				: undefined,
			textLink: {
				label: config.euTyreLink ? i18n('ngc.equipment.rims.dialog.trigger') : i18n('ngc.actions.moreInfo'),
				onTextLinkClick: () =>
					openMoreInformationDialog(
						rim as SelectableOption<FeatureCategory>,
						rim.__item,
						() => {},
						step,
						total,
						'rims',
					),
			},
			disclaimerContent: rim.__item.texts?.disclaimerText || undefined,
			disclaimerHeadline: rim.__item.texts?.disclaimerHeadline || undefined,
			disclaimerStartDate: rim.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: rim.__item.texts?.disclaimerEndDate || undefined,
		}));

		return {
			filters: sortedCategories,
			items: mappedItems,
		};
	}, [
		changeConfiguration,
		config.showPrices,
		JSON.stringify(feature),
		getName,
		isItemChecked,
		isItemLoading,
		JSON.stringify(rims),
		trackSelectionClickEvent,
	]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'rims',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'rims')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			id={'rims'}
			galleryType={'onlySingleImg'}
			galleryControls={{ compare: true }}
			media={[
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM]],
				[mediaByProfileId[ImageViewsEnum.EXTERIOR_PARALLEL_SIDE]],
				[mediaByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS]],
			]}
			headline={<OptionsHeader title={i18n('ngc.equipment.rims')} step={step} total={total} />}
			disclaimer={categoryDisclaimer}>
			<TilesSelection
				id="rims"
				items={items}
				filters={filters}
				fallbackSVG={RimFallbackSvg}
				isLoading={loadingFlags.checkingConflicts}
				step={step}
				totalSteps={total}
			/>
		</ConfigurationStep>
	);
}
